/**
 * Overridable display terminology — see design.md § 2.1.
 *
 * Nine terms: five nouns (singular + plural) and the four deliverable states
 * (label only). Resolution is system default → client → blueprint, per term.
 *
 * This is DISPLAY ONLY. It never changes an API field name, a database
 * identifier, an enum value, or a workbook sheet or column name. Export always
 * writes canonical names and canonical State values; only import is forgiving,
 * and only for states.
 */
import { z } from 'zod';
import { STATE_ORDER } from './model.js';
export const NOUN_KEYS = ['blueprint', 'room', 'pillar', 'sliver', 'deliverable'];
export const STATE_TERM_KEYS = ['draft', 'manuscript', 'proof', 'published'];
export const DEFAULT_TERMINOLOGY = {
    nouns: {
        blueprint: { singular: 'Blueprint', plural: 'Blueprints' },
        room: { singular: 'Room', plural: 'Rooms' },
        pillar: { singular: 'Pillar', plural: 'Pillars' },
        sliver: { singular: 'Sliver', plural: 'Slivers' },
        deliverable: { singular: 'Deliverable', plural: 'Deliverables' },
    },
    states: {
        draft: { label: 'Draft' },
        manuscript: { label: 'Manuscript' },
        proof: { label: 'Proof' },
        published: { label: 'Published' },
    },
};
/* ------------------------------------------------------------- validation */
// No markup, no control characters. These strings render in almost every view,
// which makes them a stored-XSS vector if treated casually (design.md R-11).
const label = z
    .string()
    .trim()
    .min(1, 'must not be empty')
    .max(40, 'must be 40 characters or fewer')
    .regex(/^[^<>\u0000-\u001F\u007F]+$/u, 'must be plain text with no markup or control characters');
const nounTerm = z.object({ singular: label, plural: label }).partial().strict();
const stateTerm = z.object({ label }).partial().strict();
export const terminologyOverrideSchema = z
    .object({
    nouns: z.object(Object.fromEntries(NOUN_KEYS.map((k) => [k, nounTerm]))).partial().strict(),
    states: z.object(Object.fromEntries(STATE_TERM_KEYS.map((k) => [k, stateTerm]))).partial().strict(),
})
    .partial()
    .strict();
const CANONICAL_STATE_LABELS = new Set(STATE_ORDER.map((s) => s.toLowerCase()));
/**
 * State labels may not collide with a canonical value or with each other — a
 * client renaming Proof to "Draft" would make the workbook's State column
 * ambiguous on import (design.md R-12). Checked against the RESOLVED set,
 * because a collision can be created by an override on either level.
 */
export function findStateLabelCollisions(t) {
    const problems = [];
    const seen = new Map();
    for (const key of STATE_TERM_KEYS) {
        const value = t.states[key].label;
        // Trimmed, because parseStateCell trims before comparing. Without it a
        // label stored as " Proof " for Manuscript passes this check, and then
        // canonical Proof wins at read time — the Manuscript label becomes
        // unreachable and the misreading is SILENT. The two functions must
        // normalise identically or the check does not check what is used.
        // Found at Gate G4.
        const norm = value.trim().toLowerCase();
        const canonicalForKey = DEFAULT_TERMINOLOGY.states[key].label.trim().toLowerCase();
        if (norm !== canonicalForKey && CANONICAL_STATE_LABELS.has(norm)) {
            problems.push(`"${value}" (for ${key}) collides with the canonical state name "${norm}" — pick a different label.`);
        }
        const clash = seen.get(norm);
        if (clash)
            problems.push(`"${value}" is used for both ${clash} and ${key}.`);
        else
            seen.set(norm, key);
    }
    return problems;
}
/* -------------------------------------------------------------- resolution */
/**
 * system default → client → blueprint, most specific wins, per term.
 * An absent key inherits; it does not blank the label.
 */
export function resolveTerms(clientOverride, blueprintOverride) {
    const pick = (...values) => values.filter((v) => typeof v === 'string' && v.length > 0).pop();
    const nouns = Object.fromEntries(NOUN_KEYS.map((k) => [
        k,
        {
            singular: pick(DEFAULT_TERMINOLOGY.nouns[k].singular, clientOverride?.nouns?.[k]?.singular, blueprintOverride?.nouns?.[k]?.singular),
            plural: pick(DEFAULT_TERMINOLOGY.nouns[k].plural, clientOverride?.nouns?.[k]?.plural, blueprintOverride?.nouns?.[k]?.plural),
        },
    ]));
    const states = Object.fromEntries(STATE_TERM_KEYS.map((k) => [
        k,
        {
            label: pick(DEFAULT_TERMINOLOGY.states[k].label, clientOverride?.states?.[k]?.label, blueprintOverride?.states?.[k]?.label),
        },
    ]));
    return { nouns, states };
}
/* ------------------------------------------------------------ import help */
const STATE_BY_KEY = {
    draft: 'Draft',
    manuscript: 'Manuscript',
    proof: 'Proof',
    published: 'Published',
};
/**
 * Resolve a workbook `State` cell. Accepts a canonical value, or — because a
 * client who renames Manuscript will have people typing the new word into the
 * spreadsheet — that blueprint's resolved state label. Canonical always wins.
 * Returns null when nothing matches, so the caller can warn.
 */
export function parseStateCell(raw, terms) {
    if (typeof raw !== 'string')
        return null;
    const norm = raw.trim().toLowerCase();
    if (!norm)
        return null;
    const canonical = STATE_ORDER.find((s) => s.toLowerCase() === norm);
    if (canonical)
        return canonical;
    for (const key of STATE_TERM_KEYS) {
        if (terms.states[key].label.trim().toLowerCase() === norm)
            return STATE_BY_KEY[key];
    }
    return null;
}
//# sourceMappingURL=terminology.js.map